import PriceInput from 'src/components/TradeForm/PriceInput/PriceInput';
import SizeInput from 'src/components/TradeForm/SizeInput/SizeInput';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import Button from 'src/components/primitives/Button/Button';
import ToggleInput from 'src/components/Inputs/ToggleInput';
import useClosePositionModal from 'src/components/Modals/ClosePositionModal/hooks/useClosePositionModal';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import { MarketType } from '@velocity-exchange/sdk';
import { UIOrderType } from '@velocity-exchange/common';
import Slider from 'src/components/primitives/Slider/Slider';
import { useState } from 'react';

export interface ClosePositionModalProps {
	marketIndex: number;
	uiOrderType: UIOrderType;
}

export default function ClosePositionModal({
	marketIndex,
	uiOrderType,
}: ClosePositionModalProps) {
	const [orderTypeToUse, setOrderTypeToUse] =
		useState<UIOrderType>(uiOrderType);

	const {
		showClosePositionConfirmation,
		priceInputValue,
		baseSizeInputValue,
		baseAssetSymbol,
		stepSize,
		errorMessage,
		handleDismiss,
		handlePriceInputChange,
		handleSizeInputChange,
		handlePositionSliderChange,
		handleToggleShowClosePositionConfirmation,
		closePosition,
		quoteSizeInputValueToUse,
		openPositionSizeDisplay,
		positionSliderValue,
		isSubmitButtonDisabled,
		isSubmitting,
		isMarketOrder,
		isMobileScreenSize,
	} = useClosePositionModal({
		marketIndex,
		uiOrderType: orderTypeToUse,
	});

	return (
		<ResponsiveModal.Container
			onClose={handleDismiss}
			dataTestId="close-position-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="close-position-modal-close">
				Close position
			</ResponsiveModal.Title>
			<ResponsiveModal.Body>
				<div className="flex justify-between typo-body-xs">
					<span className="text-text-secondary">Position</span>
					<span className="text-text-default">{openPositionSizeDisplay}</span>
				</div>

				<div className="flex flex-col gap-y-sm">
					<ToggleInput
						id="close-position-order-type-inline"
						label="Order Type"
						size={isMobileScreenSize ? 'sm' : 'md'}
						value={orderTypeToUse}
						options={[
							{ value: 'market', label: 'Market' },
							{ value: 'limit', label: 'Limit' },
						]}
						onChange={(value) => setOrderTypeToUse(value)}
					/>
					<PriceInput
						label="Price"
						value={isMarketOrder ? '' : priceInputValue}
						marketIndex={marketIndex}
						marketType={MarketType.PERP}
						handleChange={handlePriceInputChange}
						placeholder={isMarketOrder ? 'Market' : undefined}
						disabled={isMarketOrder}
						showMidButton={!isMarketOrder}
						baseAssetSymbol={baseAssetSymbol}
						dataTestId="close-position-price-input"
					/>
					<SizeInput
						baseSizeStringValue={baseSizeInputValue}
						quoteSizeStringValue={quoteSizeInputValueToUse}
						formattedMarketSymbol={baseAssetSymbol}
						onBaseSizeChange={(value) => handleSizeInputChange(value, 'base')}
						onQuoteSizeChange={(value) => handleSizeInputChange(value, 'quote')}
						stepSize={stepSize}
						sizeErrorMessage={errorMessage}
						dataTestId="close-position-size-input"
					/>
					<div className="flex items-center">
						<Slider
							value={positionSliderValue}
							onValueChange={handlePositionSliderChange}
							onValueCommit={handlePositionSliderChange}
							dataTestId="close-position-slider"
						/>
					</div>
				</div>
			</ResponsiveModal.Body>
			<ResponsiveModal.Footer className="flex-col items-stretch">
				{isMarketOrder && (
					<Checkbox.Default
						id="dont-show-close-position-confirmation"
						label="Don't show this again"
						className="w-fit"
						checked={!showClosePositionConfirmation}
						onCheckedChange={handleToggleShowClosePositionConfirmation}
						dataTestId="close-position-dont-show-checkbox"
					/>
				)}

				<Button
					size="lg"
					variant="primary"
					onClick={closePosition}
					disabled={isSubmitButtonDisabled}
					isLoading={isSubmitting}
					dataTestId="close-position-modal-submit"
				>
					Confirm
				</Button>
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
}
